/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import Table from "components/Table";
import ContentShell from "components/ContentShell";
import { Button, Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import {
  getUserTimezone,
  opacity2hex,
  serializeDateValue,
  sortArray,
  startOfWeek,
  wrapWithAll,
} from "utils/miscUtils";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import { SimpleSelectInput } from "components/FormSelectInput";
import { transactionReportColumns } from "utils/tableHelper/reportsTableHelper";
import {
  DefaultCurrency,
  PBO_InitialValues,
  RoundStatusOptions,
  TransactionTypeOptions,
} from "utils/constants";
import {
  exportReport,
  getTransactionReportList,
  ReportTypes,
} from "services/reportsService";
import { SimpleDateInput } from "components/FormDateInput";
import { SimpleTextInput } from "components/FormTextInput";
import useFilterOptionsHook from "hooks/useFilterOptions";
import { FilterSectionProps } from "types/filters";
import UploadIcon from "@mui/icons-material/Upload";
import useOpenClose from "hooks/useOpenClose";
import ExportModal from "screens/modals/ExportModal";
import { Visibility } from "@mui/icons-material";
import StatusBadge from "components/StatusBadge";
import CopyableTag from "components/CopyableTag";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: "fit-content",
    flexFlow: "row wrap",
    gap: 4,
  },
  field: {
    width: 180,
  },
  buttonContainer: {
    minWidth: 90,
    maxWidth: 120,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 24,
    marginLeft: 10,
    gap: 5,
  },
  button: {
    height: 30,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    textTransform: "none",
    width: "100% !important",
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
      maxWidth: "100% !important",
    },
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  playerCardParent: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  icon: {
    [theme.breakpoints.down("sm")]: {
      fontSize: `14px !important`,
    },
  },
  amountCell: {
    fontWeight: "700 !important",
    fontSize: "13px !important",
    color: theme.palette.primary.main,
  },
  winAmountCell: {
    fontWeight: "700 !important",
    fontSize: "13px !important",
    color: "#4caf50",
  },
  betAmountCell: {
    fontWeight: "700 !important",
    fontSize: "13px !important",
    color: "#ff5722",
  },
  idCell: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "40px",
  },
}));

const RowActions = ({ data, actionHandlers }: any) => {
  const navigate = useNavigate();

  const handleRowAction = (data: any) => {
    navigate(
      configData.routes.reports.roundDetails + `?betTxnId=${data?.betTxnId}`
    );
  };

  return (
    <Visibility
      onClick={() => handleRowAction(data)}
      sx={{
        cursor: "pointer",
        color: "#1976d2",
        fontSize: "26px",
        "&:hover": {
          color: "#03053eff",
        },
      }}
    />
  );
};

const FilterSection = ({
  handleChange = () => {},
  onSubmitClick = () => {},
  onReset = () => {},
  filterOptions,
}: FilterSectionProps) => {
  const classes = useStyles();
  const customStyle = { height: 30 };
  return (
    <Box className={classes.root}>
      <Box className={classes.field} style={{ marginLeft: "0px" }}>
        <SimpleDateInput
          label="From:"
          id="startTime"
          name="startTime"
          fullWidth
          format="DD-MM-YYYY"
          variant="inline"
          removeBottomMargin={true}
          value={filterOptions.startTime}
          onChange={(date) => handleChange({ name: "startTime", value: date })}
          customStyle={customStyle}
        />
      </Box>
      <Box className={classes.field}>
        <SimpleDateInput
          label="To:"
          id="endTime"
          name="endTime"
          fullWidth
          format="DD-MM-YYYY"
          variant="inline"
          removeBottomMargin={true}
          value={filterOptions.endTime}
          onChange={(date) => handleChange({ name: "endTime", value: date })}
          customStyle={customStyle}
        />
      </Box>

      <Box className={classes.field}>
        <SimpleSelectInput
          name="platform"
          label="Platform"
          placeholdertext="Select Platforms"
          options={wrapWithAll(sessionHelper.platformOptions)}
          value={filterOptions.platform}
          onChange={(e) =>
            handleChange({ name: "platform", value: e?.target?.value })
          }
          customStyle={customStyle}
        />
      </Box>
      {![null, "all"].includes(filterOptions.platform) && (
        <Box className={classes.field}>
          <SimpleSelectInput
            name="operator"
            label="Operator"
            options={wrapWithAll(
              sessionHelper.getFilteredOperators({
                platformIds: [filterOptions.platform],
              })
            )}
            value={filterOptions.operator}
            placeholder="Select Operators"
            onChange={(e) =>
              handleChange({ name: "operator", value: e?.target?.value })
            }
            customStyle={customStyle}
          />
        </Box>
      )}
      {![null, "all"].includes(filterOptions.operator) && (
        <Box className={classes.field}>
          <SimpleSelectInput
            name="brand"
            label="Brand"
            options={wrapWithAll(
              sessionHelper.getFilteredBrands({
                platformIds: [filterOptions.platform],
                operatorIds: [filterOptions.operator],
              })
            )}
            value={filterOptions.brand}
            onChange={({ target: { name, value } }) =>
              handleChange({ name, value })
            }
            customStyle={customStyle}
          />
        </Box>
      )}
      {/* <Box className={classes.field}>
        <SimpleSelectInput
          name="currency"
          label="Currency"
          options={CurrencyOptions}
          value={filterOptions.currency}
          onChange={({ target: { name, value } }) =>
            handleChange({ name, value })
          }
          customStyle={customStyle}
        />
      </Box> */}
      <Box className={classes.field}>
        <SimpleSelectInput
          name="transactionType"
          label="Transaction Type"
          options={TransactionTypeOptions}
          value={filterOptions.transactionType}
          onChange={({ target: { name, value } }) =>
            handleChange({ name, value })
          }
          customStyle={customStyle}
        />
      </Box>
      <Box className={classes.field}>
        <SimpleSelectInput
          name="status"
          label="Status"
          options={RoundStatusOptions}
          value={filterOptions.status}
          onChange={({ target: { name, value } }) =>
            handleChange({ name, value })
          }
          customStyle={customStyle}
        />
      </Box>
      <Box className={classes.field}>
        <SimpleTextInput
          onChange={({ target: { name, value } }) =>
            handleChange({ name, value })
          }
          customStyle={{ height: 30 }}
          label="Round ID"
          name="roundId"
          value={filterOptions.roundId}
        />
      </Box>
      <Box className={classes.field}>
        <SimpleTextInput
          onChange={(e) =>
            handleChange({ name: "betId", value: e?.target?.value })
          }
          customStyle={{ height: 30 }}
          label="Bet Txn ID"
          name="betId"
          value={filterOptions.betId}
        />
      </Box>
      <Box className={classes.field}>
        <SimpleTextInput
          onChange={(e) =>
            handleChange({
              name: "platformTransactionId",
              value: e?.target?.value,
            })
          }
          customStyle={{ height: 30 }}
          label="Ext. Bet Ticket ID"
          name="platformTransactionId"
          value={filterOptions.platformTransactionId}
        />
      </Box>
      <Box className={classes.field}>
        <SimpleTextInput
          onChange={({ target: { name, value } }) =>
            handleChange({ name, value })
          }
          customStyle={{ height: 30 }}
          type="text"
          label="External Player Id"
          name="externalPlayerId"
          value={filterOptions.externalPlayerId}
        />
      </Box>
      <Box className={classes.buttonContainer}>
        <Button className={classes.button} onClick={onSubmitClick}>
          Submit
        </Button>
        <Button className={classes.button} onClick={onReset}>
          Reset
        </Button>
      </Box>
    </Box>
  );
};

const TransactionReport = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const [orderDirection, setOrderDirection] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.PLAYER.CARD.UPDATE_ROLES
  );
  const [columns, setColumns] = useState([]);
  const {
    filterOptions,
    onChange: setFilterOptions,
    reset,
  } = useFilterOptionsHook({
    initialValues: {
      startTime: startOfWeek(),
      endTime: new Date(),
      currency: DefaultCurrency,
      externalPlayerId: null,
      roundId: null,
      betId: null,
      platformTransactionId: null,
      transactionType: "all",
      status: "all",
      ...PBO_InitialValues,
    },
  });
  const { isOpen, open, close } = useOpenClose();

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: list?.length });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...list], orderDirection, column);
    setList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const buildReportPayload = () => {
    const { startTime, endTime, ...rest } = filterOptions;
    let _start = new Date(startTime);
    let _end = new Date(endTime);
    if (_start > _end) {
      showSnackbar("Start date should not be greater than end date", "error");
      return false;
    }

    return {
      ...rest,
      pageNo,
      pageSize,
      startTime: serializeDateValue(startTime),
      endTime: serializeDateValue(endTime),
      timezone: getUserTimezone(),
    };
  };

  const fetchPlayerReport = async () => {
    setLoading(true);
    try {
      const payload = buildReportPayload();
      if (!payload) {
        setLoading(false);
        return;
      }

      const response = await getTransactionReportList(payload);
      const summary = response?.data?.transactionSummary;

      setList(summary);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error?.message ?? error, "error");
    }
  };

  const handleFilterChange = ({ name, value }: any) =>
    setFilterOptions({ name, value });

  const onSendExportedDataEmail = async (email) => {
    setLoading(true);
    try {
      let payload = buildReportPayload();
      if (!payload) {
        setLoading(false);
        return;
      }

      const { platform, operator, brand, transactionType, status } = payload;

      if (platform === "all") payload.platform = null;
      if (operator === "all") payload.operator = null;
      if (brand === "all") payload.brand = null;
      if (transactionType === "all") payload.transactionType = null;
      if (status === "all") payload.status = null;

      payload = { ...payload, type: ReportTypes.txn, email };

      await exportReport(payload);

      setLoading(false);
      showSnackbar("Data request, will be sent to the email.");
    } catch (error) {
      setLoading(false);
      showSnackbar(error?.message ?? error, "error");
    }
  };

  useEffect(() => {
    const initialColumns = transactionReportColumns(filterOptions)
      .filter((col) => col.id !== "actions" || hasEditPermission)
      .map((col) => {
        // Add custom render for Internal Bet Txn ID as copyable tag
        if (col.id === "betTxnId") {
          return {
            ...col,
            renderCell: (data: any) => (
              <Box className={styles.idCell}>
                <CopyableTag value={data.betTxnId} />
              </Box>
            ),
          };
        }

        // Add custom render for bet status
        if (col.id === "resolved") {
          return {
            ...col,
            renderCell: (data: any) => (
              <StatusBadge value={data.resolved} type="resolved" />
            ),
          };
        }

        // Add custom render for round status
        if (col.id === "status") {
          return {
            ...col,
            renderCell: (data: any) => (
              <StatusBadge value={data.status} type="roundStatus" />
            ),
          };
        }

        // Add custom render for transaction type
        if (col.id === "transactionType") {
          return {
            ...col,
            renderCell: (data: any) => (
              <StatusBadge
                value={data.transactionType}
                type="transactionType"
              />
            ),
          };
        }

        // Highlight bet amount
        if (col.id === "betAmount") {
          return {
            ...col,
            renderCell: (data: any) => (
              <Box className={styles.betAmountCell}>
                {data.betAmount != null ? data.betAmount.toFixed(2) : "-"}
              </Box>
            ),
          };
        }

        // Highlight win amount
        if (col.id === "winAmount") {
          return {
            ...col,
            renderCell: (data: any) => (
              <Box className={styles.winAmountCell}>
                {data.winAmount != null ? data.winAmount.toFixed(2) : "-"}
              </Box>
            ),
          };
        }

        // Bold Player Name
        if (col.id === "playerName") {
          return {
            ...col,
            renderCell: (data: any) => (
              <Box sx={{ fontWeight: "700", fontSize: "12px" }}>
                {data.playerName || "-"}
              </Box>
            ),
          };
        }
        return col;
      });

    setColumns(initialColumns);
    fetchPlayerReport();
  }, [pageNo, pageSize]);

  return (
    <ContentShell
      title="Transactional Report"
      loading={loading}
      actions={
        <Button
          startIcon={<UploadIcon className={styles.icon} />}
          variant="contained"
          onClick={open}
        >
          Export
        </Button>
      }
    >
      <Box className={styles.playerCardParent}>
        <FilterSection
          filterOptions={filterOptions}
          handleChange={handleFilterChange}
          onSubmitClick={fetchPlayerReport}
          onReset={reset}
        />
        <Table
          columns={columns}
          data={list}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={list.length}
          highlightAltRow={true}
          actions={RowActions}
          onColumnClick={onColumnClick}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          actionHandlers={{}}
        />
      </Box>
      <ExportModal
        list={list}
        fileName="Transaction_Report"
        filterOptions={filterOptions}
        open={isOpen}
        onClose={close}
        onSendEmail={onSendExportedDataEmail}
      />
    </ContentShell>
  );
};

export default TransactionReport;
